import React from 'react';
import { VStack, Box } from '@gluestack-ui/themed';
import { FormProvider, useForm } from 'react-hook-form';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useAuthChangePassword } from '../../api/endpoints/auth/auth';
import { useAuthStore } from '../../auth/store';
import { useDialog } from '../../context/DialogContext';
import { useApiErrorReporter } from '../../utils/apiErrorReporter';
import FormField from '../../components/forms/FormField';
import { PopupDialog } from '../../components/PopupDialog';

export const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const changePasswordMutation = useAuthChangePassword();
  const user = useAuthStore((state) => state.user);

  const methods = useForm({
    defaultValues: {
      username: user?.username || '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    reset,
    setError,
    getValues,
  } = methods;

  const onSubmit = async (data) => {
    try {
      await changePasswordMutation.mutateAsync(
        {
          data: {
            oldPassword: data.oldPassword,
            newPassword: data.newPassword,
          },
        },
        {
          onSuccess: () => {
            showToastMessage('success', t('reset-password.success'));
            reset();
            onClose();
          },
          onError: (error) => {
            // Must match AuthErrors in api/app/schemas/api/auth.py (FLARE-6B).
            const errorDetails = reportApiError(
              error,
              t('reset-password.title'),
              'toast',
              {
                expectedErrorCodes: [
                  'AUTH_PASSWORD_POLICY_VIOLATED',
                  'AUTH_INVALID_CREDENTIALS',
                  'AUTH_USER_CHANGED',
                ],
              },
            );
            if (errorDetails.code === 'AUTH_PASSWORD_POLICY_VIOLATED') {
              setError('newPassword', { message: t(`errors.${errorDetails.code.toLowerCase()}`) });
            } else if (errorDetails.code === 'AUTH_INVALID_CREDENTIALS') {
              setError('oldPassword', { message: t(`errors.${errorDetails.code.toLowerCase()}`) });
            }
          },
        },
      );
    } catch (error) {
      // Errors are handled in onError callback
    }
  };

  const buttons = [
    {
      text: t('common.buttons.cancel'),
      variant: 'outline',
      action: 'secondary',
      onPress: onClose,
    },
    {
      text: t('common.buttons.save'),
      action: 'primary',
      onPress: handleSubmit(onSubmit),
      isDisabled: changePasswordMutation.isPending,
      preventAutoClose: true,
    },
  ];

  return (
    <PopupDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('reset-password.title')}
      message=""
      buttons={buttons}
    >
      <FormProvider {...methods}>
        <Box as={Platform.OS === 'web' ? 'form' : 'div'}>
          {/* Hidden username field for password managers */}
          <Box style={{ display: 'none' }}>
            <FormField
              name="username"
              type="text"
              autoComplete="username"
            />
          </Box>

          <VStack space="md">
            <FormField
              name="oldPassword"
              type="password"
              label={t('login.password')}
              isRequired
              autoComplete="current-password"
              rules={{ required: t('reset-password.errors.required') }}
            />

            <FormField
              name="newPassword"
              type="password"
              label={t('reset-password.new-password')}
              isRequired
              autoComplete="new-password"
              rules={{ required: t('reset-password.errors.required') }}
            />

            <FormField
              name="confirmPassword"
              type="password"
              label={t('reset-password.confirm-password')}
              isRequired
              autoComplete="new-password"
              rules={{
                required: t('reset-password.errors.required'),
                validate: (value) => (
                  value === getValues('newPassword') || t('reset-password.errors.password-mismatch')
                ),
              }}
            />
          </VStack>
        </Box>
      </FormProvider>
    </PopupDialog>
  );
};

export default ChangePasswordModal;
